"use client";

import clsx from "clsx";
import { useId } from "react";
import { themeImage } from "@/lib/media";
import { useModals } from "../ModalProvider";
import {
  bodyClass,
  footerClass,
  headClass,
  inputClass,
  labelClass,
  messageBodyClass,
  messageClass,
  messageHeadClass,
  rowClass,
  submitButtonClass,
  submitClass,
} from "./authFormClasses";
import { fieldValue, useAuthForm } from "./useAuthForm";

/**
 * The port of fragments/auth/signin-form.php and signin-form.js.
 *
 * On WordPress this posted to poppy_ajax_signin, which checked the email and
 * password against the DAS app's API, created or updated a matching WordPress
 * user, signed them in and sent back the app URL to redirect to. Here it goes
 * through submitForm("signin").
 *
 * The labels pointed at an id nothing had ("signin-form"); they point at their
 * inputs here.
 */
export default function SigninForm() {
  const { openModal } = useModals();
  const id = useId();
  const { message, success, onSubmit, stateAttributes } = useAuthForm(
    "signin",
    (form) => ({
      email: fieldValue(form, "email"),
      password: fieldValue(form, "password"),
    }),
  );

  return (
    <form
      action=""
      className="group/form data-[loading=true]:pointer-events-none data-[loading=true]:opacity-50 js-signin-form"
      id={`signin-form-${id}`}
      onSubmit={onSubmit}
      {...stateAttributes}
    >
      <div className={headClass}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={themeImage("logo-dark.png")} alt="DAS Academies" />
      </div>

      <div className={messageClass}>
        <h3 className={`${messageHeadClass} js-form-message-head`}>
          {message?.header || ""}
        </h3>

        <p className={`${messageBodyClass} js-form-message-body`}>
          {message?.body || ""}
        </p>
      </div>

      {/* On success the form goes and only the message stays. */}
      {!success && (
        <>
          <div className={`${bodyClass} js-form-body`}>
            <div className={rowClass}>
              <div>
                <label htmlFor="signin-form-email" className={labelClass}>
                  Email
                </label>

                <input
                  type="email"
                  id="signin-form-email"
                  name="email"
                  className={inputClass}
                  placeholder="Enter your email address"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className={rowClass}>
              <div>
                <label htmlFor="signin-form-password" className={labelClass}>
                  Password
                </label>

                <input
                  type="password"
                  id="signin-form-password"
                  name="password"
                  className={inputClass}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>
          </div>

          <div className={`${submitClass} js-form-submit`}>
            <button
              type="submit"
              className={clsx(
                submitButtonClass,
                "group-data-[loading=true]/form:pointer-events-none group-data-[loading=true]/form:opacity-50",
              )}
            >
              Login
            </button>
          </div>

          <div className={`${footerClass} js-form-foot`}>
            <p>
              Don&apos;t have an account?{" "}
              <a
                href="#"
                className="js-trigger-modal"
                onClick={(event) => {
                  event.preventDefault();
                  openModal("register-modal");
                }}
              >
                Register
              </a>
            </p>

            <p>
              <a
                href="#"
                className="js-trigger-modal"
                onClick={(event) => {
                  event.preventDefault();
                  openModal("forgot-password-modal");
                }}
              >
                Forgot Password?
              </a>
            </p>
          </div>
        </>
      )}
    </form>
  );
}
